import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReportDialog from '../ReportDialog';
import DiagnosisReport from './DiagnosisReport';
import { usePatientContext } from '../../SubPatinetProvider';
import { Diagnosis, getDiagnosisInfo } from '../../../pages/Registration/NewRegistration/PatientPartnerRegistration/PatientReport/reportHelper';
import { generatePDF } from '../../../utils/reportUtils';
import { Typography } from '@mui/material';

interface Props {
  open: boolean;
  onClose: () => void;
}

const DiagnosisReportGen: React.FC<Props> = ({ open, onClose }) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { patientId } = usePatientContext();
  const { t: tDiagnosis } = useTranslation('investigation_config');
  const { t: tGeneral } = useTranslation('general');
  const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([]);

  // Fetch diagnoses for a patient with error handling using Promises
  function fetchDiagnoses(patientId: string): Promise<Diagnosis[]> {
    return getDiagnosisInfo(`patient_id=${patientId}`)
      .then((data) => {
        // eslint-disable-next-line no-console
        console.log('Fetched diagnosis data:', data);
        return data?.objects || [];
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch diagnoses:', error);
        return [];
      });
  }

  useEffect(() => {
    if (open && patientId) {
      fetchDiagnoses(String(patientId)).then(setDiagnoses);
    }
  }, [open, patientId]);

  const handleDownloadPDF = () => {
    if (reportRef.current) {
      generatePDF(reportRef.current, tDiagnosis('Diagnosis_Report', { defaultValue: 'Diagnosis Report' }));
    }
  };

  const handlePrint = () => window.print();

  return (
    <ReportDialog
      open={open}
      onClose={onClose}
      title={tDiagnosis('Diagnosis_Report', { defaultValue: 'Diagnosis Report' })}
      onDownload={diagnoses.length > 0 ? handleDownloadPDF : undefined}
      onPrint={diagnoses.length > 0 ? handlePrint : undefined}
      showActions={diagnoses.length > 0}
      downloadLabel={tGeneral('DownloadReport', { defaultValue: 'Download Report' })}
      printLabel={tGeneral('PrintReport', { defaultValue: 'Print Report' })}
    >
      <div ref={reportRef}>
        {diagnoses.length > 0 ? (
          <DiagnosisReport diagnoses={diagnoses} />
        ) : (
          <Typography variant="h6" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            {tGeneral('dataNotAvailable', { defaultValue: 'Data is not available' })}
          </Typography>
        )}
      </div>
    </ReportDialog>
  );
};

export default DiagnosisReportGen;
